import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import HomeView from './views/HomeView';
import RewardsView from './views/RewardsView';
import AdminView from './views/AdminView';
import ChildModal from './components/ChildModal';
import Nav from './components/Nav';

// Same-origin: the server serves this build. Dev uses the "proxy" in package.json.
const API = '/api';

const TITLES = {
  home: 'The Board · Token Archive',
  rewards: 'The Shelf · Token Archive',
  admin: 'The Stockroom · Token Archive',
};

function App() {
  const [view, setView] = useState('home');
  const [children, setChildren] = useState([]);
  const [spendingChoices, setSpendingChoices] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);
  const [landedId, setLandedId] = useState(null);

  const headingRef = useRef(null);
  const firstRender = useRef(true);

  const fetchChildren = useCallback(async () => {
    const res = await fetch(`${API}/children`);
    setChildren(await res.json());
  }, []);

  const fetchChoices = useCallback(async () => {
    const res = await fetch(`${API}/spending-choices`);
    setSpendingChoices(await res.json());
  }, []);

  useEffect(() => {
    Promise.all([fetchChildren(), fetchChoices()]).finally(() => setLoading(false));
  }, [fetchChildren, fetchChoices]);

  // A view change is a navigation: name the page and put focus at the top of it.
  useEffect(() => {
    document.title = TITLES[view];
    if (firstRender.current) { firstRender.current = false; return; }
    headingRef.current?.focus();
  }, [view]);

  const goTo = (next) => {
    if (next === view) return;
    if (document.startViewTransition) document.startViewTransition(() => setView(next));
    else setView(next);
  };

  const addTokens = async (childId, amount, description) => {
    const res = await fetch(`${API}/children/${childId}/add-tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, description }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Those tokens did not go in. Try again.');
    setChildren(prev => prev.map(c => (c.id === childId ? data : c)));
    setLandedId(childId);
    return data;
  };

  const spendTokens = async (childId, spendingChoiceId) => {
    const res = await fetch(`${API}/children/${childId}/spend-tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ spending_choice_id: spendingChoiceId }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(
        data.error === 'Not enough tokens'
          ? 'Not enough tokens in the box for that one yet.'
          : data.error || 'That did not go through. Try again.'
      );
    }
    setChildren(prev => prev.map(c => (c.id === childId ? data : c)));
    setLandedId(childId);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setView('home');
  };

  if (loading) {
    return (
      <div className="boot">
        <p className="boot-mark">Token<br />Archive</p>
        <div className="boot-bar" />
        <p>Opening the stockroom</p>
      </div>
    );
  }

  return (
    <div className="app">
      <a className="skip-link" href="#main">Skip to content</a>

      <Nav view={view} setView={goTo} adminToken={adminToken} logout={logout} />

      <main className="main" id="main">
        {view === 'home' && (
          <HomeView
            children={children}
            rewardCount={spendingChoices.length}
            landedId={landedId}
            onSelectChild={setSelectedChild}
            onGoAdmin={() => goTo('admin')}
            headingRef={headingRef}
          />
        )}
        {view === 'rewards' && (
          <RewardsView
            spendingChoices={spendingChoices}
            onGoAdmin={() => goTo('admin')}
            headingRef={headingRef}
          />
        )}
        {view === 'admin' && (
          <AdminView
            children={children}
            spendingChoices={spendingChoices}
            adminToken={adminToken}
            setAdminToken={(t) => {
              setAdminToken(t);
              if (t) localStorage.setItem('adminToken', t);
            }}
            onRefresh={() => { fetchChildren(); fetchChoices(); }}
            API={API}
            headingRef={headingRef}
          />
        )}
      </main>

      {selectedChild && (
        <ChildModal
          child={selectedChild}
          spendingChoices={spendingChoices}
          onClose={() => setSelectedChild(null)}
          onAddTokens={addTokens}
          onSpendTokens={spendTokens}
          API={API}
        />
      )}
    </div>
  );
}

export default App;
